import { useState, useEffect } from "react";
import { Box, Typography, Chip } from "@mui/material";
import { programs } from "../data/data";

const SDG_COLORS = ["#E5243B","#DDA63A","#4C9F38","#C5192D","#FF3A21","#26BDE2","#FCC30B","#A21942","#FD6925","#DD1367","#FD9D24","#BF8B2E","#3F7E44","#0A97D9","#56C02B","#00689D","#19486A"];

const programImages = {
  " Solid Stabilized Earth Block":      "/panafrica1.png",   // ← updated image
  "Biogas Project":         "/Biogas.jpg",
  "Future Water Solutions": "/purewater.jpg",
  "Scholarship Chapter":    "/weber.jpg",
};

/* ─── Detailed content for all 4 programs ─────────────────── */
const programDetails = {

  /* ── UPDATED: Pan-Africa Butterfly / SEB ── */
  " Solid Stabilized Earth Block": {
    sdgTag: "SDG 11 — Sustainable Cities",
    intro: `The Pan-Africa Butterfly is a high-performance architectural system designed to solve the challenges of modern African living. By combining a rigorous 3600mm structural grid with a signature V-roof, this prototype provides a universal "Chassis" that remains structurally consistent while performing optimally across the continent's diverse climates.`,
    problem: "Millions of African families live without adequate shelter. Conventional building materials are expensive, carbon-heavy, and often imported — while locally available earth resources remain largely untapped. BTSA's approach turns that around by engineering dignity from the ground up.",
    objectives: [
      {
        number: "01",
        title: "The Universal Chassis",
        body: "A rigorous 3600mm structural grid combined with a signature V-roof creates a single, adaptable framework — structurally consistent across every African climate zone without changing materials.",
      },
      {
        number: "02",
        title: "The Universal Skin — Foundation Layer",
        body: "The lower 2400mm of every wall is built from Solid Stabilized Earth Block (SEB) — providing essential thermal mass, structural integrity, and climate resilience from fully local, low-carbon materials.",
      },
      {
        number: "03",
        title: "Adapting Through Geometry",
        body: "Unlike traditional housing that changes materials per climate zone, the Pan-Africa Butterfly uses geometry — wall angles, roof pitch, and aperture placement — to regulate heat, airflow, and water across diverse environments.",
      },
      {
        number: "04",
        title: "Off-Grid by Design",
        body: "Each unit is designed to operate independently of centralised infrastructure — integrating passive cooling, rainwater harvesting, and solar readiness as standard features of the chassis.",
      },
    ],
    highlights: [
      { icon: "🧱", label: "Core Material",    value: "Stabilized Earth Blocks (SEB)" },
      { icon: "📐", label: "Structural Grid",  value: "3600mm modular chassis" },
      { icon: "🌍", label: "Design Scope",     value: "Pan-African climate adaptability" },
      { icon: "🎯", label: "SDG Alignment",    value: "SDG 11 — Sustainable Cities & Communities" },
    ],
    contactPrompt: "Interested in supporting or replicating this project in your community?",
    contactEmail: "africabuildtoshelter@gmail.com",
  },

  "Biogas Project": {
    sdgTag: "SDGs 7 & 9 — Clean Energy & Innovation",
    intro: `Energy poverty is a daily reality for millions of African households — forcing families to rely on expensive, polluting fuels that harm health, drain incomes, and accelerate deforestation. BTSA's Biogas Project brings clean, renewable energy directly to underserved communities by converting organic waste into a practical energy source for cooking and lighting.`,
    problem: "Lack of access to affordable, clean energy keeps communities trapped in a cycle of poverty. Charcoal and kerosene cause indoor air pollution, destroy forests, and consume a disproportionate share of household income — particularly for women and children who bear the burden of fuel collection.",
    objectives: [
      {
        number: "01",
        title: "Clean Energy Access",
        body: "Install biogas systems that convert household and agricultural organic waste into clean cooking gas and electricity, reducing dependence on wood fuel and charcoal.",
      },
      {
        number: "02",
        title: "Health & Environmental Impact",
        body: "Eliminate harmful indoor smoke from open fires and reduce deforestation driven by charcoal production, directly improving respiratory health and forest cover.",
      },
      {
        number: "03",
        title: "Economic Savings for Households",
        body: "Free families from the recurring cost of purchasing firewood, charcoal, or kerosene — redirecting those savings toward food, education, and healthcare.",
      },
      {
        number: "04",
        title: "Local Technical Capacity",
        body: "Train community members to install, operate, and maintain biogas digesters, creating local green jobs and ensuring long-term sustainability beyond the initial project.",
      },
    ],
    highlights: [
      { icon: "", label: "Energy Source",       value: "Organic waste → biogas" },
      { icon: "", label: "Target Beneficiaries", value: "Underserved households & communities" },
      { icon: "", label: "Status",               value: "Ongoing" },
      { icon: "", label: "SDG Alignment",        value: "SDGs 7 & 9 — Clean Energy & Innovation" },
    ],
    contactPrompt: "Want to bring the Biogas Project to your community or partner with us?",
    contactEmail: "africabuildtoshelter@gmail.com",
  },

  "Future Water Solutions": {
    sdgTag: "SDG 6 — Clean Water & Sanitation",
    intro: `Safe water is the foundation of health, dignity, and development — yet millions across Africa walk hours daily to collect water that may still make them sick. BTSA's Future Water Solutions program is developing community-scale water purification initiatives to make clean, sustainable water a reality for underserved communities across the continent.`,
    problem: "Lack of access to safe water drives preventable disease, keeps children — especially girls — out of school, and limits economic productivity. Rural and peri-urban communities bear the heaviest burden, often relying on contaminated sources with no affordable alternative.",
    objectives: [
      {
        number: "01",
        title: "Safe Water Access",
        body: "Deploy practical, community-scale water purification systems that remove biological and chemical contaminants, providing reliable access to safe drinking water at the household and community level.",
      },
      {
        number: "02",
        title: "Sustainable Water Management",
        body: "Introduce water harvesting and conservation practices that help communities manage seasonal water scarcity and build resilience against climate-driven drought.",
      },
      {
        number: "03",
        title: "Health & Sanitation Improvement",
        body: "Reduce waterborne illness — a leading cause of child mortality in Africa — by ensuring that the water communities drink, cook with, and wash in meets safe standards.",
      },
      {
        number: "04",
        title: "Community Ownership",
        body: "Train local water committees to operate and maintain systems independently, ensuring solutions remain functional and community-owned long after BTSA's initial intervention.",
      },
    ],
    highlights: [
      { icon: "", label: "Focus Area",           value: "Water purification & safe access" },
      { icon: "", label: "Target Beneficiaries", value: "Underserved communities across Africa" },
      { icon: "", label: "Status",               value: "Upcoming initiative" },
      { icon: "", label: "SDG Alignment",        value: "SDG 6 — Clean Water & Sanitation" },
    ],
    contactPrompt: "Interested in supporting or partnering on water solutions?",
    contactEmail: "africabuildtoshelter@gmail.com",
  },

  "Scholarship Chapter": {
    sdgTag: "SDG 4 — Quality Education",
    intro: `The BTSA Scholarship Chapter is a peer-driven learning circle exclusively for Build To Shelter Africa members. Members collaborate to identify scholarship opportunities, sharpen their applications, and grow the professional skills that open doors — in academia, in careers, and in community leadership.`,
    objectives: [
      {
        number: "01",
        title: "Collaborative Educational Growth",
        body: "Members pool knowledge and effort to help each other secure scholarships and further-study opportunities — sharing leads, reviewing applications, and holding each other accountable through the process.",
      },
      {
        number: "02",
        title: "Soft-Skills Development",
        body: "Through expert-led online webinars, members learn to write compelling personal statements, scholarship essays, and professional CVs that genuinely stand out in competitive pools.",
      },
      {
        number: "03",
        title: "Professional Communication",
        body: "The chapter builds written and verbal communication confidence — preparing members to navigate academic interviews, workplace environments, and public speaking with clarity and poise.",
      },
      {
        number: "04",
        title: "Digital Literacy & Personal Branding",
        body: "Members master social media tools for professional self-promotion and digital marketing, equipping them to represent themselves — and BTSA — powerfully in today's networked world.",
      },
    ],
    planOfAction: [
      "Launch with training webinars featuring scholarship experts from across Africa",
      "Daily scholarship updates shared within the member group",
      "At least one scholarship application completed per member per week",
      "Personalised support for members facing application challenges",
      "Active participation tracked to keep momentum high",
    ],
    challenge: {
      title: "Youth Innovation Challenge 2026",
      subtitle: "World Engineering Day · East Africa",
      description:
        "In celebration of World Engineering Day 2026, BTSA launched the Youth Innovation Challenge — calling innovators aged 18–35 to propose sustainable engineering solutions for vulnerable communities. The response was extraordinary: from AI-monitored wetland systems to bio-composite modular shelters, young engineers across the region proved that the future of sustainable infrastructure is already here.",
      winners: [
        {
          medal: "",
          place: "1st Place",
          title: "Smart Subsurface Constructed Wetland",
          team: "Desire Chirichoga & Team · NUST Zimbabwe",
          detail: "A dual-substrate wetland using industrial waste (Platinum Furnace Slag & Alum Sludge) to remediate heavy metals and eutrophication — complete with AI-powered monitoring and a 100-year design life.",
        },
        {
          medal: "",
          place: "2nd Place",
          title: "The Osteo-Poly Link",
          team: "Trevor Maina Ndirangu · Civil Structural Engineer",
          detail: "A bio-composite modular system using recycled HDPE plastic and ground animal bone to create tool-free interlocking panels for dignified sanitation facilities — a perfect circular-economy solution.",
        },
        {
          medal: "",
          place: "3rd Place",
          title: "EcoResilient Homes",
          team: "Shiro Gathura",
          detail: "Climate-adaptive housing using Stabilized Soil Blocks with raised foundations, rainwater harvesting, and passive cooling — delivering 45% cost savings while tackling flooding, heat, and water scarcity.",
        },
      ],
    },
    enrollLink: "https://forms.gle/NvF89Ev4FGLrQgkX7",
    note: "Open exclusively to current BTSA members.",
  },
};

/* ── Modal ─────────────────────────────────────────────────── */
function ProgramModal({ program, idx, onClose }) {
  const d = programDetails[program.name];
  const accentA = SDG_COLORS[idx * 3 % SDG_COLORS.length];
  const accentB = SDG_COLORS[(idx * 3 + 1) % SDG_COLORS.length];

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", esc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <Box
      onClick={(e) => { if (e.currentTarget === e.target) onClose(); }}
      sx={{
        position: "fixed", inset: 0, zIndex: 1400,
        bgcolor: "rgba(10,4,0,0.70)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        overflowY: "auto", p: { xs: 0, md: 4 },
      }}
    >
      <Box sx={{
        bgcolor: "#F7F4EF", width: "100%", maxWidth: 760,
        borderRadius: { xs: 0, md: "10px" }, overflow: "hidden",
      }}>

        {/* Hero */}
        <Box sx={{ position: "relative", height: { xs: 180, md: 240 }, overflow: "hidden" }}>
          <Box component="img" src={programImages[program.name]} alt={program.name}
            sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
          />
          <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(26,10,0,0.85) 0%, transparent 60%)" }} />
          <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: `linear-gradient(90deg,${accentA},${accentB})` }} />
          <Box
            component="button" onClick={onClose}
            sx={{
              position: "absolute", top: 14, right: 16,
              width: 32, height: 32, borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)",
              color: "#fff", fontSize: "18px", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
            }}
          >×</Box>
          <Box sx={{ position: "absolute", bottom: 18, left: 24 }}>
            {d.sdgTag && (
              <Chip label={d.sdgTag} size="small" sx={{
                bgcolor: "rgba(255,255,255,0.15)", color: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
                fontFamily: "'Space Mono', monospace", fontSize: "9px",
                letterSpacing: "1px", textTransform: "uppercase", height: 20, mb: 0.75,
              }} />
            )}
            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "22px", md: "28px" }, fontWeight: 700, color: "#fff" }}>
              {program.name}
            </Typography>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ px: { xs: 3, md: 5 }, pt: 4, pb: 5 }}>

          {/* Intro */}
          <Typography sx={{ fontSize: "14.5px", color: "rgba(26,10,0,0.7)", lineHeight: 1.85, mb: 4 }}>
            {d.intro}
          </Typography>

          {/* Problem statement — only for non-scholarship programs */}
          {d.problem && (
            <Box sx={{ bgcolor: "#fff", border: "1px solid rgba(26,10,0,0.09)", borderLeft: `3px solid ${accentA}`, borderRadius: "0 6px 6px 0", p: 2.5, mb: 4 }}>
              <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "9px", letterSpacing: "2px", textTransform: "uppercase", color: "rgba(26,10,0,0.32)", mb: 1 }}>The Problem We're Solving</Typography>
              <Typography sx={{ fontSize: "13px", color: "rgba(26,10,0,0.65)", lineHeight: 1.78 }}>{d.problem}</Typography>
            </Box>
          )}

          {/* Objectives */}
          <ModalLabel>Our Approach</ModalLabel>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mb: 4 }}>
            {d.objectives.map((o) => (
              <Box key={o.number} sx={{
                bgcolor: "#fff", border: "1px solid rgba(26,10,0,0.09)",
                borderRadius: "6px", p: 2.5, position: "relative", overflow: "hidden",
                "&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: `linear-gradient(90deg,${accentA},${accentB})` },
              }}>
                <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "9px", letterSpacing: "2px", color: "rgba(26,10,0,0.25)", mb: 0.5 }}>{o.number}</Typography>
                <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "13.5px", fontWeight: 700, color: "#1a0a00", mb: 0.75, lineHeight: 1.3 }}>{o.title}</Typography>
                <Typography sx={{ fontSize: "12px", color: "rgba(26,10,0,0.58)", lineHeight: 1.75 }}>{o.body}</Typography>
              </Box>
            ))}
          </Box>

          {/* Quick facts — for non-scholarship programs */}
          {d.highlights && (
            <>
              <ModalLabel>Quick Facts</ModalLabel>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 1.5, mb: 4 }}>
                {d.highlights.map((h, i) => (
                  <Box key={i} sx={{ bgcolor: "#fff", border: "1px solid rgba(26,10,0,0.09)", borderRadius: "6px", p: 2, textAlign: "center" }}>
                    <Typography sx={{ fontSize: "18px", mb: 0.75 }}>{h.icon}</Typography>
                    <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "8.5px", letterSpacing: "1.5px", textTransform: "uppercase", color: "rgba(26,10,0,0.32)", mb: 0.5 }}>{h.label}</Typography>
                    <Typography sx={{ fontSize: "11.5px", color: "#1a0a00", fontWeight: 700, lineHeight: 1.4 }}>{h.value}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Plan of Action — scholarship only */}
          {d.planOfAction && (
            <>
              <ModalLabel>Plan of Action</ModalLabel>
              <Box sx={{ bgcolor: "#fff", border: "1px solid rgba(26,10,0,0.09)", borderRadius: "6px", p: 3, mb: 4 }}>
                {d.planOfAction.map((item, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1.75, alignItems: "flex-start", mb: i < d.planOfAction.length - 1 ? 1.75 : 0 }}>
                    <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: "#5C3311", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", mt: "1px" }}>
                      <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "8px", fontWeight: 700, color: "#fff", lineHeight: 1 }}>{String(i + 1).padStart(2, "0")}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: "13px", color: "rgba(26,10,0,0.65)", lineHeight: 1.72 }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Youth Innovation Challenge — scholarship only */}
          {d.challenge && (
            <>
              <ModalLabel>Featured Achievement</ModalLabel>
              <Box sx={{ border: "1px solid rgba(26,10,0,0.11)", borderRadius: "8px", overflow: "hidden", mb: 4 }}>
                <Box sx={{ bgcolor: "#1a0a00", px: 3, py: 2.5 }}>
                  <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "17px", fontWeight: 700, color: "#fff" }}>{d.challenge.title}</Typography>
                  <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "9px", letterSpacing: "2px", color: "rgba(255,255,255,0.42)", textTransform: "uppercase", mt: 0.5 }}>{d.challenge.subtitle}</Typography>
                </Box>
                <Box sx={{ bgcolor: "#faf8f4", px: 3, pt: 2.5, pb: 1 }}>
                  <Typography sx={{ fontSize: "13px", color: "rgba(26,10,0,0.62)", lineHeight: 1.85, mb: 2.5 }}>{d.challenge.description}</Typography>
                  {d.challenge.winners.map((w, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 2, bgcolor: "#fff", border: "1px solid rgba(26,10,0,0.08)", borderRadius: "6px", p: 2, mb: 1.5 }}>
                      <Typography sx={{ fontSize: "20px", lineHeight: 1, mt: "2px", flexShrink: 0 }}>{w.medal}</Typography>
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 0.25 }}>
                          <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "9px", letterSpacing: "2px", color: "#DDA63A", textTransform: "uppercase", fontWeight: 700 }}>{w.place}</Typography>
                          <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "13.5px", fontWeight: 700, color: "#1a0a00" }}>{w.title}</Typography>
                        </Box>
                        <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "9px", letterSpacing: "1px", color: "rgba(26,10,0,0.36)", mb: 0.75 }}>{w.team}</Typography>
                        <Typography sx={{ fontSize: "12px", color: "rgba(26,10,0,0.6)", lineHeight: 1.72 }}>{w.detail}</Typography>
                      </Box>
                    </Box>
                  ))}
                  <Typography sx={{ fontSize: "12px", color: "rgba(26,10,0,0.45)", fontStyle: "italic", pt: 1, pb: 2.5 }}>
                    Future challenges are planned — follow BTSA to be part of the next cohort.
                  </Typography>
                </Box>
              </Box>
            </>
          )}

          {/* CTA */}
          <Box sx={{
            bgcolor: "#1a0a00", borderRadius: "8px", p: 3,
            display: "flex", flexDirection: { xs: "column", sm: "row" },
            alignItems: { sm: "center" }, justifyContent: "space-between", gap: 2,
          }}>
            <Box>
              <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "16px", fontWeight: 700, color: "#fff", mb: 0.5 }}>
                {d.enrollLink ? "Ready to join?" : "Get Involved"}
              </Typography>
              <Typography sx={{ fontSize: "11.5px", color: "rgba(255,255,255,0.42)", fontStyle: "italic" }}>
                {d.note || d.contactPrompt}
              </Typography>
            </Box>
            <Box
              component="a"
              href={d.enrollLink || `mailto:${d.contactEmail}`}
              target={d.enrollLink ? "_blank" : undefined}
              rel={d.enrollLink ? "noopener noreferrer" : undefined}
              sx={{
                display: "inline-block", bgcolor: "#DDA63A", color: "#1a0a00",
                fontFamily: "'Space Mono', monospace", fontSize: "10px", fontWeight: 700,
                letterSpacing: "2px", textTransform: "uppercase", textDecoration: "none",
                px: 3, py: 1.5, borderRadius: "4px", whiteSpace: "nowrap",
                transition: "all 0.2s", "&:hover": { bgcolor: "#F5BC4A" },
              }}
            >
              {d.enrollLink ? "Enroll Now →" : "Contact Us →"}
            </Box>
          </Box>

        </Box>
      </Box>
    </Box>
  );
}

function ModalLabel({ children }) {
  return (
    <Typography sx={{
      fontFamily: "'Space Mono', monospace", fontSize: "9px",
      letterSpacing: "3px", textTransform: "uppercase",
      color: "rgba(26,10,0,0.32)", mb: 1.5,
      display: "flex", alignItems: "center", gap: 1,
      "&::after": { content: '""', flex: 1, height: "1px", bgcolor: "rgba(26,10,0,0.10)" },
    }}>
      {children}
    </Typography>
  );
}

/* ── Main ─────────────────────────────────────────────────── */
export default function Programs() {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <Box id="programs" sx={{ bgcolor: "#F7F4EF", position: "relative", overflow: "hidden" }}>

      {/* Grain texture */}
      <Box sx={{
        position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0, opacity: 0.5,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E")`,
        backgroundSize: "128px",
      }} />

      {/* Top label bar */}
      <Box sx={{
        borderBottom: "1px solid rgba(26,10,0,0.12)",
        px: { xs: 3, md: 6 }, py: 2,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "relative", zIndex: 1,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{
            width: 6, height: 6, borderRadius: "50%", bgcolor: "#5C3311",
            animation: "pulse 2s ease-in-out infinite",
            "@keyframes pulse": { "0%,100%": { opacity: 1, transform: "scale(1)" }, "50%": { opacity: 0.4, transform: "scale(0.8)" } },
          }} />
          <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", color: "rgba(26,10,0,0.45)" }}>
            What We Do
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "10px", letterSpacing: "2px", color: "rgba(26,10,0,0.30)" }}>
          {String(programs.length).padStart(2, "0")} Programs
        </Typography>
      </Box>

      {/* Section header */}
      <Box sx={{
        px: { xs: 3, md: 6 }, pt: { xs: 5, md: 6 }, pb: { xs: 4, md: 5 },
        display: "flex", flexDirection: { xs: "column", md: "row" },
        alignItems: { md: "flex-end" }, justifyContent: "space-between", gap: 3,
        position: "relative", zIndex: 1,
        borderBottom: "1px solid rgba(26,10,0,0.10)",
      }}>
        <Box>
          <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", color: "#5C3311", mb: 2, fontWeight: 700 }}>
            → Community Initiatives
          </Typography>
          <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "36px", md: "52px", lg: "60px" }, fontWeight: 700, color: "#1a0a00", lineHeight: 1.05, letterSpacing: "-1.5px" }}>
            Programs building
            <br />
            <Box component="span" sx={{ position: "relative", "&::after": { content: '""', position: "absolute", bottom: 6, left: 0, width: "100%", height: "3px", background: "linear-gradient(90deg,#E5243B,#DDA63A,#4C9F38)", borderRadius: "2px" } }}>
              a better Africa.
            </Box>
          </Typography>
        </Box>
        <Box sx={{ maxWidth: 320 }}>
          <Typography sx={{ fontSize: "14px", color: "rgba(26,10,0,0.52)", lineHeight: 1.78, mb: 3 }}>
            Community-driven initiatives tackling housing, energy, water, and education across the continent.
          </Typography>
          <Box sx={{ display: "flex", height: "3px", borderRadius: "2px", overflow: "hidden" }}>
            {SDG_COLORS.map((c, i) => <Box key={i} sx={{ flex: 1, bgcolor: c }} />)}
          </Box>
        </Box>
      </Box>

      {/* Program Cards */}
      <Box sx={{ px: { xs: 3, md: 6 }, pt: { xs: 5, md: 6 }, pb: { xs: 6, md: 8 }, position: "relative", zIndex: 1 }}>
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
          gap: { xs: "1px", md: "1px" },
          border: "1px solid rgba(26,10,0,0.10)", borderRadius: "8px",
          overflow: "hidden", bgcolor: "rgba(26,10,0,0.08)",
        }}>
          {programs.map((p, idx) => (
            <Box key={p.name} sx={{
              bgcolor: "#F7F4EF", display: "flex", flexDirection: "column",
              overflow: "hidden", transition: "all 0.35s ease", cursor: "default",
              "&:hover": { bgcolor: "#fff" },
              "&:hover .prog-img": { transform: "scale(1.05)" },
              "&:hover .prog-title": { color: "#5C3311" },
              "&:hover .prog-rule": { width: "48px" },
            }}>
              <Box sx={{ overflow: "hidden", aspectRatio: "4/3", position: "relative", flexShrink: 0 }}>
                <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", zIndex: 2, background: `linear-gradient(90deg, ${SDG_COLORS[idx * 3 % SDG_COLORS.length]}, ${SDG_COLORS[(idx * 3 + 1) % SDG_COLORS.length]})` }} />
                <Typography sx={{ position: "absolute", top: 12, left: 14, fontFamily: "'Space Mono', monospace", fontSize: "10px", fontWeight: 700, color: "rgba(255,255,255,0.75)", letterSpacing: "2px", zIndex: 3, textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>
                  {String(idx + 1).padStart(2, "0")}
                </Typography>
                <Box component="img" className="prog-img" src={programImages[p.name]} alt={p.name}
                  sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block", transition: "transform 0.75s cubic-bezier(0.25,0.46,0.45,0.94)" }}
                />
              </Box>

              <Box sx={{ p: { xs: 2.5, md: 3 }, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                {p.sdg && (
                  <Chip label={p.sdg} size="small" sx={{
                    alignSelf: "flex-start", bgcolor: "rgba(92,51,17,0.08)", color: "#5C3311",
                    border: "1px solid rgba(92,51,17,0.20)", fontFamily: "'Space Mono', monospace",
                    fontSize: "9px", letterSpacing: "1px", textTransform: "uppercase", mb: 1.5, height: 22,
                  }} />
                )}
                <Typography className="prog-title" sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "17px", md: "18px", lg: "19px" }, fontWeight: 700, color: "#1a0a00", lineHeight: 1.25, mb: 1, transition: "color 0.3s ease" }}>
                  {p.name}
                </Typography>
                <Box className="prog-rule" sx={{ width: "24px", height: "2px", bgcolor: "#DDA63A", borderRadius: "1px", mb: 1.5, transition: "width 0.4s cubic-bezier(0.25,0.46,0.45,0.94)" }} />
                <Typography sx={{ fontSize: "13px", color: "rgba(26,10,0,0.55)", lineHeight: 1.75, flexGrow: 1 }}>
                  {p.desc}
                </Typography>

                <Typography
                  component="button"
                  onClick={() => setOpenIdx(idx)}
                  sx={{
                    mt: 2.5, fontFamily: "'Space Mono', monospace", fontSize: "10px",
                    fontWeight: 700, color: "#5C3311",
                    letterSpacing: "1.5px", textTransform: "uppercase",
                    alignSelf: "flex-start",
                    background: "none", border: "none", borderBottom: "1.5px solid #5C3311",
                    cursor: "pointer", p: 0, transition: "all 0.2s",
                    "&:hover": { color: "#DDA63A", borderColor: "#DDA63A" },
                  }}
                >
                  Learn More →
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Bottom bar */}
      <Box sx={{
        borderTop: "1px solid rgba(26,10,0,0.10)",
        px: { xs: 3, md: 6 }, py: 2.5,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 2, position: "relative", zIndex: 1,
      }}>
        <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "10px", color: "rgba(26,10,0,0.35)", letterSpacing: "2px", textTransform: "uppercase" }}>
          Youth-led · Pan-African · Est. 2025
        </Typography>
        <Typography component="a" href="#contact" sx={{
          fontFamily: "'Space Mono', monospace", fontSize: "11px", fontWeight: 700,
          color: "#5C3311", textDecoration: "none", letterSpacing: "1.5px", textTransform: "uppercase",
          borderBottom: "1.5px solid #5C3311", pb: "1px", transition: "all 0.2s",
          "&:hover": { color: "#DDA63A", borderColor: "#DDA63A" },
        }}>
          Get Involved →
        </Typography>
      </Box>

      {/* Modal */}
      {openIdx !== null && (
        <ProgramModal
          program={programs[openIdx]}
          idx={openIdx}
          onClose={() => setOpenIdx(null)}
        />
      )}
    </Box>
  );
}